import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getMe, login as loginApi, signup as signupApi } from "../services/auth";

type AuthContextValue = {
  user: { id: string; name?: string | null } | null;
  token: string | null;
  idToken?: string | null;
  isLoading: boolean;
  isReady: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  completeExternalLogin: (accessToken: string, idToken?: string | null) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "earlyspot_auth_token";
const STORAGE_ID_TOKEN = "earlyspot_id_token";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [idToken, setIdToken] = useState<string | null>(() => localStorage.getItem(STORAGE_ID_TOKEN));
  const [user, setUser] = useState<{ id: string; name?: string | null } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(!token);

  // Hydrate user from stored token
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      console.log("[Auth] Hydrate effect running, token present:", !!token);
      
      // If we don't have a token, we can't hydrate
      if (!token) {
        setUser(null);
        setIsReady(true);
        return;
      }

      // If we already have user data, we assume it's valid for this session
      // This prevents the race condition where completeExternalLogin sets the user
      // but this effect runs and clears it because /auth/me fails.
      if (user) {
        console.log("[Auth] User already present in state, skipping hydration call.");
        setIsReady(true);
        return;
      }

      // Try to decode idToken to prefill friendly name/email
      const decodedFromId = decodeIdToken(idToken);

      setIsReady(false);
      try {
        console.log("[Auth] Calling getMe to hydrate user...");
        const data = await getMe(token);
        console.log("[Auth] Hydration successful:", data);
        if (!cancelled) {
          // Prefer name/email from idToken if backend doesn't include them
          const hydratedName = data.name ?? decodedFromId.name ?? decodedFromId.email;
          const hydratedId = decodedFromId.email || data.userId;
          setUser({ id: hydratedId, name: hydratedName });
        }
      } catch (err) {
        console.error("[Auth] Hydration failed:", err);
        if (!cancelled) {
          console.warn("[Auth] Clearing invalid token due to hydration failure");
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_ID_TOKEN);
          setUser(null);
          setToken(null);
          setIdToken(null);
        }
      } finally {
        if (!cancelled) {
          setIsReady(true);
        }
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [token, user, idToken]); // Add user and idToken to dependency array to re-evaluate when user is set

  const handleAuthSuccess = useCallback((nextToken: string, userId: string, name?: string | null, nextIdToken?: string | null) => {
    console.log("[Auth] handleAuthSuccess - Setting user:", { userId, name });
    setToken(nextToken);
    localStorage.setItem(STORAGE_KEY, nextToken);
    if (nextIdToken) {
      setIdToken(nextIdToken);
      localStorage.setItem(STORAGE_ID_TOKEN, nextIdToken);
    }
    setUser({ id: userId, name });
    // Force immediate ready state
    setIsReady(true);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const res = await loginApi(email, password);
        handleAuthSuccess(res.token, res.userId, res.name);
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthSuccess]
  );

  const signup = useCallback(
    async (email: string, password: string, name?: string) => {
      setIsLoading(true);
      try {
        const res = await signupApi(email, password, name);
        handleAuthSuccess(res.token, res.userId, res.name);
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthSuccess]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_ID_TOKEN);
    setToken(null);
    setIdToken(null);
    setUser(null);
  }, []);

  const decodeIdToken = (idToken?: string | null): { sub?: string; email?: string; name?: string } => {
    if (!idToken) {
      console.log("[Auth] No idToken to decode");
      return {};
    }
    try {
      const payload = idToken.split(".")[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
      console.log("[Auth] Decoded idToken:", { sub: decoded.sub, email: decoded.email, name: decoded.name });
      return { sub: decoded.sub, email: decoded.email, name: decoded.name };
    } catch (err) {
      console.error("[Auth] Failed to decode idToken:", err);
      return {};
    }
  };

  const completeExternalLogin = useCallback(
    async (accessToken: string, idToken?: string | null) => {
      console.log("[Auth] completeExternalLogin called");
      console.log("[Auth] accessToken present:", !!accessToken);
      console.log("[Auth] idToken present:", !!idToken);
      
      const decoded = decodeIdToken(idToken);
      
      // Try to get user info from idToken first
      if (decoded.sub) {
        // Use email as userId if available, otherwise use sub
        const userId = decoded.email || decoded.sub;
        console.log("[Auth] Using decoded token - userId:", userId, "name:", decoded.name);
        handleAuthSuccess(accessToken, userId, decoded.name, idToken);
        return;
      }

      // Fallback: ask backend who we are
      console.log("[Auth] No sub in idToken, calling backend /auth/me");
      try {
        setIsLoading(true);
        const me = await getMe(accessToken);
        console.log("[Auth] Backend returned:", me);
        handleAuthSuccess(accessToken, me.userId, me.name, idToken);
      } catch (err) {
        console.error("[Auth] Backend /auth/me failed:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthSuccess]
  );

  const value = useMemo(
    () => ({
      user,
      token,
      idToken,
      isLoading,
      isReady,
      login,
      signup,
      logout,
      completeExternalLogin,
    }),
    [user, token, idToken, isLoading, isReady, login, signup, logout, completeExternalLogin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};

